import { useState, useEffect } from "react";
import { getStandClosures, createStandClosure, updateStandClosure, deleteStandClosure } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { 
  CalendarClock, Plus, Edit2, Trash2, Loader2, Ban, CheckCircle2, Clock, AlertTriangle
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

// Fallback list used only if the parent doesn't supply the configured stands.
const FALLBACK_STANDS = [
  "1A", "1B", "2A", "2B", "3A", "3B", "4A", "4B",
  "17B", "17C", "18B", "18C",
  "30", "31", "32", "33", "34", "35", "36", "37", "38", "39"
];

export default function StandClosureScheduler({ onClosureChange, stands }) {
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    stand_id: "",
    start_date: "",
    end_date: "",
    reason: ""
  });

  // Fetch closures
  const fetchClosures = async () => {
    setLoading(true);
    try {
      const response = await getStandClosures();
      setClosures(response.data.closures || []);
    } catch (error) {
      console.error("Failed to fetch closures:", error);
      toast.error("Failed to load scheduled closures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClosures();
  }, []);

  // Handle form changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      stand_id: "",
      start_date: "",
      end_date: "",
      reason: ""
    });
  };

  // Create new closure
  const handleCreate = async () => {
    if (!formData.stand_id || !formData.start_date || !formData.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      await createStandClosure(formData);
      toast.success(`Closure scheduled for stand ${formData.stand_id}`);
      setShowAddDialog(false);
      resetForm();
      fetchClosures();
      onClosureChange?.();
    } catch (error) {
      console.error("Failed to create closure:", error);
      toast.error(error.response?.data?.detail || "Failed to schedule closure");
    } finally {
      setSaving(false);
    }
  };

  // Update closure
  const handleUpdate = async () => {
    if (!selectedClosure) return;

    setSaving(true);
    try {
      await updateStandClosure(selectedClosure.id, {
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason
      });
      toast.success("Closure updated successfully");
      setShowEditDialog(false);
      setSelectedClosure(null);
      resetForm();
      fetchClosures();
      onClosureChange?.();
    } catch (error) {
      console.error("Failed to update closure:", error);
      toast.error(error.response?.data?.detail || "Failed to update closure");
    } finally {
      setSaving(false);
    }
  };

  // Delete closure
  const handleDelete = async () => {
    if (!selectedClosure) return;

    setSaving(true);
    try {
      await deleteStandClosure(selectedClosure.id);
      toast.success("Closure deleted and stand reactivated");
      setShowDeleteConfirm(false);
      setSelectedClosure(null);
      fetchClosures();
      onClosureChange?.();
    } catch (error) {
      console.error("Failed to delete closure:", error);
      toast.error(error.response?.data?.detail || "Failed to delete closure");
    } finally {
      setSaving(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (closure) => {
    setSelectedClosure(closure);
    setFormData({
      stand_id: closure.stand_id,
      start_date: closure.start_date,
      end_date: closure.end_date,
      reason: closure.reason || ""
    });
    setShowEditDialog(true);
  };

  // Open delete confirm
  const openDeleteConfirm = (closure) => {
    setSelectedClosure(closure);
    setShowDeleteConfirm(true);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-red-600 text-white"><Ban size={12} className="mr-1" /> Active - Closed</Badge>;
      case "upcoming":
        return <Badge className="bg-amber-500 text-white"><Clock size={12} className="mr-1" /> Upcoming</Badge>;
      case "expired":
        return <Badge variant="outline" className="text-gray-500"><CheckCircle2 size={12} className="mr-1" /> Completed</Badge>;
      case "inactive":
        return <Badge variant="outline" className="text-gray-400">Inactive</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get today's date for min attribute
  const today = new Date().toISOString().split('T')[0];

  // Use the actual configured stands from the parent (Apron map). Falls back to
  // a static list only if none were supplied. Sorted naturally (numeric-aware).
  const standList = (Array.isArray(stands) && stands.length > 0 ? [...stands] : FALLBACK_STANDS)
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

  return (
    <Card className="bg-white dark:bg-[#1e293b]" data-testid="stand-closure-scheduler">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <CalendarClock size={20} className="text-[#d4af37]" />
            Scheduled Stand Closures
          </span>
          <Button
            onClick={() => {
              resetForm();
              setShowAddDialog(true);
            }}
            size="sm"
            className="gap-2 bg-[#d4af37] hover:bg-[#b8962d]"
            data-testid="add-closure-btn"
          >
            <Plus size={16} />
            Schedule Closure
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin mr-2" size={24} />
            Loading schedules...
          </div>
        ) : closures.length === 0 ? (
          <div className="text-center py-8 text-[#64748b]">
            <CalendarClock size={40} className="mx-auto mb-2 opacity-30" />
            <p>No scheduled closures</p>
            <p className="text-sm mt-1">Click "Schedule Closure" to add a new one</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {closures.map((closure) => (
              <div
                key={closure.id}
                className={`p-3 rounded-lg border transition-all ${
                  closure.status === "active" 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800"
                    : closure.status === "upcoming"
                      ? "border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800"
                      : "border-gray-200 dark:border-gray-700"
                }`}
                data-testid={`closure-${closure.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#d4af37] text-white font-bold">{closure.stand_id}</Badge>
                    {getStatusBadge(closure.status)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => openEditDialog(closure)}
                      data-testid={`edit-closure-${closure.id}`}
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => openDeleteConfirm(closure)}
                      data-testid={`delete-closure-${closure.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-[#64748b] dark:text-gray-400">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">Period:</span>
                    <span>
                      {format(parseISO(closure.start_date), "MMM d, yyyy")} - {format(parseISO(closure.end_date), "MMM d, yyyy")}
                    </span>
                  </div>
                  {closure.reason && (
                    <div className="flex items-start gap-2">
                      <span className="font-medium">Reason:</span>
                      <span className="italic">{closure.reason}</span>
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    Created by {closure.created_by} on {format(parseISO(closure.created_at), "MMM d, yyyy HH:mm")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add Closure Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-white dark:bg-[#1e293b]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock size={20} className="text-[#d4af37]" />
              Schedule Stand Closure
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Parking Stand *</Label>
              <Select
                value={formData.stand_id}
                onValueChange={(v) => handleInputChange("stand_id", v)}
              >
                <SelectTrigger data-testid="stand-select">
                  <SelectValue placeholder="Select stand..." />
                </SelectTrigger>
                <SelectContent>
                  {standList.map((stand) => (
                    <SelectItem key={stand} value={stand}>{stand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange("start_date", e.target.value)}
                  min={today}
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange("end_date", e.target.value)}
                  min={formData.start_date || today}
                  data-testid="end-date-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                placeholder="e.g., Maintenance, Construction, Event..."
                value={formData.reason}
                onChange={(e) => handleInputChange("reason", e.target.value)}
                data-testid="reason-input"
              />
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg text-sm text-amber-700 dark:text-amber-400">
              <AlertTriangle size={16} className="inline mr-2" />
              The stand will be automatically closed on the start date and reopened after the end date.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={saving || !formData.stand_id || !formData.start_date || !formData.end_date}
              className="bg-[#d4af37] hover:bg-[#b8962d]"
              data-testid="save-closure-btn"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Schedule Closure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Closure Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white dark:bg-[#1e293b]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 size={20} className="text-[#d4af37]" />
              Edit Closure - Stand {selectedClosure?.stand_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange("start_date", e.target.value)}
                  data-testid="edit-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange("end_date", e.target.value)}
                  min={formData.start_date}
                  data-testid="edit-end-date-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Input
                placeholder="e.g., Maintenance, Construction, Event..."
                value={formData.reason}
                onChange={(e) => handleInputChange("reason", e.target.value)}
                data-testid="edit-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={saving || !formData.start_date || !formData.end_date}
              className="bg-[#d4af37] hover:bg-[#b8962d]"
              data-testid="update-closure-btn"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Update Closure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 size={20} className="text-red-600" />
              Delete Scheduled Closure
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this scheduled closure for <strong>Stand {selectedClosure?.stand_id}</strong>?
              <br /><br />
              {selectedClosure?.status === "active" && (
                <span className="text-amber-600 font-medium">
                  This closure is currently active. The stand will be immediately reopened.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedClosure(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
              data-testid="confirm-delete-btn"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Delete Closure
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
